/**
 * Дни недели как битовая маска.
 *
 * Бит 0 — понедельник (в России неделя начинается с него), бит 6 — воскресенье.
 * Маска хранится одним числом: правило «завтрак только по будням» не требует
 * ни отдельной таблицы, ни массива в записи.
 *
 * Модуль лежит в общем слое, потому что маску одинаково используют режим дня,
 * задачи, привычки и курсы лекарств. Реализаций было четыре, и они успели
 * разойтись: одна и та же маска показывалась как «по будням» у лекарства,
 * «По будням» у задачи и «Пн, Вт, Ср, Чт, Пт» у привычки — у привычек коротких
 * формулировок не было вовсе. Побитовая проверка была переписана ещё в девяти
 * местах.
 */

export const WEEKDAY_LABELS = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'] as const;

export const EVERY_DAY_MASK = 0b1111111;
export const WORKDAYS_MASK = 0b0011111;
export const WEEKEND_MASK = 0b1100000;

/** Список индексов дней, входящих в маску. */
export function maskToDays(mask: number): number[] {
  const days: number[] = [];
  for (let day = 0; day < 7; day += 1) {
    if (hasWeekday(mask, day)) days.push(day);
  }
  return days;
}

export function countDays(mask: number): number {
  return maskToDays(mask).length;
}

export function hasWeekday(mask: number, index: number): boolean {
  return (mask & (1 << index)) !== 0;
}

export function toggleWeekday(mask: number, index: number): number {
  return mask ^ (1 << index);
}

/**
 * Человеческое описание маски: «Каждый день», «По будням», «Пн, Ср, Пт».
 *
 * `lower` нужен там, где описание встраивается в строку («принимать по
 * будням»): регистр — это оформление, а не отдельное правило, и ради него
 * заводить вторую функцию не стоит.
 */
export function describeWeekdays(mask: number, options: { lower?: boolean } = {}): string {
  const text = describe(mask);
  if (!options.lower) return text;
  return text.charAt(0).toLowerCase() + text.slice(1);
}

function describe(mask: number): string {
  if (mask === EVERY_DAY_MASK) return 'Каждый день';
  if (mask === WORKDAYS_MASK) return 'По будням';
  if (mask === WEEKEND_MASK) return 'По выходным';
  if (mask === 0) return 'Никогда';

  return WEEKDAY_LABELS.filter((_, index) => hasWeekday(mask, index)).join(', ');
}
